import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/sell-my-commercial-property-kitchener-waterloo',
  title: 'Sell My Commercial Property — Kitchener-Waterloo Region',
  description:
    'Sell a commercial or industrial property across Kitchener, Waterloo, Cambridge or Guelph. Direct representation from Pinnacle Commercial — no fabricated numbers, no listings portal.',
};

const FAQS: Faq[] = [
  {
    q: 'How much is my commercial property worth?',
    a: "That takes an actual look at your property, its tenancy (if any), and recent comparable activity — not a number pulled from a generic online estimator. The first conversation is exactly that: a real assessment, not a guess.",
  },
  {
    q: 'How long does it typically take to sell?',
    a: "I won't put a number of weeks on that here — it depends on the property type, condition, price point, and how the market's moving when you list, and a fixed timeline promised in advance is usually a number someone made up. What I can do is walk you through what's actually driving speed for your specific property.",
  },
  {
    q: 'Do you handle both owner-occupied and investment (tenanted) properties?',
    a: 'Yes — the process and the buyer pool differ between the two, and part of the first conversation is figuring out which situation you\'re in and what that means for positioning.',
  },
  {
    q: 'What areas do you cover for sellers?',
    a: 'Cambridge, Kitchener, Waterloo and Guelph, and the surrounding Waterloo Region and Wellington County — inside one practice rather than referred out to someone unfamiliar with the specific market.',
  },
];

export default function SellCommercialPropertyKW() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-sell-commercial-kw', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-03"
      kicker="Local search — selling commercial property"
      h1="Sell your commercial property, Kitchener-Waterloo region."
      intro="Representation for owners selling commercial, industrial, or investment property across Cambridge, Kitchener, Waterloo and Guelph — a real assessment of your property, not a form-filled estimate."
      glyph="office"
      audiences={[
        {
          label: 'Ready to sell',
          body: "Tell me about the property — type, size, whether it's tenanted — and I'll walk you through positioning, pricing, and what a realistic process looks like for your specific situation.",
          ctaLabel: 'Start the conversation',
        },
        {
          label: 'Weighing your options',
          body: "Not ready to list yet? A conversation about what your property is actually worth and what selling would involve costs you nothing and commits you to nothing.",
          ctaLabel: 'Ask a question first',
        },
      ]}
      localLabel="Kitchener-Waterloo region, commercial sales"
      localBody={
        <>
          <p>
            The Kitchener-Waterloo region's commercial base spans converted-industrial and new-build office
            downtown, ION-corridor and university-adjacent space in Waterloo, and industrial stock along the
            401 running through Cambridge — which means buyer interest for a given property depends heavily on
            exactly where and what it is, not just the regional average.
          </p>
          <p>
            Selling well here starts with an honest read on your specific property and the buyers actually
            active for that type right now, not a generic valuation model.
          </p>
        </>
      }
      honestLabel="No invented numbers"
      honestBody="You won't get a made-up timeline in weeks or a valuation pulled from a formula here — those are numbers I don't have until I've actually looked at your property and the current market for it. What you get instead is a straight assessment based on what's real."
      faqs={FAQS}
      interest="buying-selling"
      closingLine="Selling a commercial property in the KW region — start with a real conversation."
      related={[
        { to: '/buying-selling', label: 'Full buying & selling services' },
        { to: '/about', label: 'About Pinnacle Commercial' },
        { to: '/buying-or-selling-a-business-guelph', label: 'Buying or selling a business — Guelph' },
      ]}
    />
  );
}
